import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { 
  ArrowLeft,
  Share2,
  X,
  Loader2,
  Calendar,
  ChevronRight,
  Phone,
  Mail,
  MessageCircle,
  Twitter,
  Facebook,
  Linkedin,
  LinkIcon,
  QrCode,
  MoreHorizontal
} from 'lucide-react';
const QRCodeSVG = lazy(() => 
  import('qrcode.react').then(module => ({ default: module.QRCodeSVG }))
);
import { ChatInterface } from './ChatInterface';
import { themes } from '../utils/themes';
import { subscribeWithEmail, getSubscriberCount } from '../utils/subscriptions';
import { normalizeImageUrl } from '../cloudinaryConfig';
import { PLATFORMS_CONFIG } from '../utils/platforms.jsx';
import { defaultAvatarDataUrl } from '../utils/defaultAvatar';

export const PublicView = ({ profileData, onBack, profileOwnerId, user }) => {
  const [showChat, setShowChat] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showFloatingText, setShowFloatingText] = useState(false);
  const [activeTab, setActiveTab] = useState('links'); // 'links', 'videos', or 'store'

  // Clean up links with duplicate prefixes (fixes existing data issues)
  const cleanedLinks = React.useMemo(() => {
    if (!profileData.links || !Array.isArray(profileData.links)) return [];
    
    return profileData.links.map(link => {
      if (!link.url) return link;
      
      const platform = PLATFORMS_CONFIG.find(p => p.id === link.iconId);
      if (!platform || !platform.prefix) {
        // No platform found or no prefix - return link as-is
        return link;
      }
      
      let cleanedUrl = link.url.trim();
      const originalUrl = cleanedUrl;
      
      // Extract username from the URL using platform-specific patterns
      // This handles both normal URLs and duplicate prefix URLs
      const platformPatterns = {
        instagram: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^\/\?\s#]+)/gi,
        tiktok: /(?:https?:\/\/)?(?:www\.)?(?:vm\.)?tiktok\.com\/@?([^\/\?\s#]+)/gi,
        threads: /(?:https?:\/\/)?(?:www\.)?threads\.net\/@?([^\/\?\s#]+)/gi,
        twitter: /(?:https?:\/\/)?(?:www\.)?(?:x\.com|twitter\.com)\/([^\/\?\s#]+)/gi,
        facebook: /(?:https?:\/\/)?(?:www\.|m\.)?facebook\.com\/(?:profile\.php\?id=)?([^\/\?\s&#]+)/gi,
        snapchat: /(?:https?:\/\/)?(?:www\.)?snapchat\.com\/@([^\/\?\s#]+)/gi,
        twitch: /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([^\/\?\s#]+)/gi,
        patreon: /(?:https?:\/\/)?(?:www\.)?patreon\.com\/([^\/\?\s#]+)/gi,
        linkedin: /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:in|company)\/([^\/\?\s#]+)/gi,
        calendly: /(?:https?:\/\/)?(?:www\.)?calendly\.com\/([^\/\?\s#]+)/gi,
      };
      
      const pattern = platformPatterns[platform.id];
      if (pattern) {
        // Reset regex lastIndex to ensure we get all matches
        pattern.lastIndex = 0;
        const matches = [...cleanedUrl.matchAll(pattern)];
        
        if (matches.length > 0) {
          // For Snapchat, use @ symbol extraction (new format: snapchat.com/@username)
          if (platform.id === 'snapchat') {
            // Find the last @ symbol in the entire URL (most reliable for Snapchat)
            const lastAtIndex = cleanedUrl.lastIndexOf('@');
            if (lastAtIndex !== -1) {
              // Extract everything after the last @
              const afterAt = cleanedUrl.substring(lastAtIndex + 1);
              // Clean up: remove query params, fragments, slashes
              const cleanUsername = afterAt.split('?')[0].split('#')[0].split('/')[0].trim();
              
              if (cleanUsername && cleanUsername.length > 0 && !cleanUsername.startsWith('http') && cleanUsername !== 'https:' && cleanUsername !== 'http:') {
                cleanedUrl = platform.prefix + cleanUsername;
                if (originalUrl !== cleanedUrl) {
                  console.log(`[Link Cleaner] ${platform.name}:`, originalUrl, '->', cleanedUrl);
                }
              }
            }
          } else {
            // Get the last match (most likely the actual username, especially if there are duplicates)
            const lastMatch = matches[matches.length - 1];
            const username = lastMatch[1];
            
            if (username && username !== 'https:' && username !== 'http:') {
              // Clean username: remove query params, fragments, trailing slashes, @ symbols
              const cleanUsername = username.split('?')[0].split('#')[0].split('/')[0].replace(/^@+/, '').trim();
              
              // Only rebuild if we have a valid username (not a protocol)
              if (cleanUsername && cleanUsername.length > 0 && !cleanUsername.startsWith('http')) {
                // Always rebuild with the platform prefix to ensure consistency
                // This fixes duplicate prefixes and ensures correct format
                cleanedUrl = platform.prefix + cleanUsername;
                
                // Log for debugging
                if (originalUrl !== cleanedUrl) {
                  console.log(`[Link Cleaner] ${platform.name}:`, originalUrl, '->', cleanedUrl);
                }
              }
            }
          }
        } else {
          // If pattern didn't match, try to extract username by removing known prefixes
          // This is a fallback for edge cases
          let tempUrl = cleanedUrl;
          const prefixVariations = [
            platform.prefix,
            platform.prefix.replace('https://', 'https://www.'),
            platform.prefix.replace('https://', 'http://'),
            platform.prefix.replace('https://', 'http://www.'),
          ];
          
          for (const prefixVar of prefixVariations) {
            if (tempUrl.toLowerCase().includes(prefixVar.toLowerCase())) {
              // Find the last occurrence of the prefix
              const lastIndex = tempUrl.toLowerCase().lastIndexOf(prefixVar.toLowerCase());
              if (lastIndex !== -1) {
                const afterPrefix = tempUrl.substring(lastIndex + prefixVar.length);
                const potentialUsername = afterPrefix.split('?')[0].split('#')[0].split('/')[0].replace(/^@+/, '').trim();
                if (potentialUsername && !potentialUsername.startsWith('http')) {
                  cleanedUrl = platform.prefix + potentialUsername;
                  console.log(`[Link Cleaner Fallback] ${platform.name}:`, originalUrl, '->', cleanedUrl);
                  break;
                }
              }
            }
          }
        }
      }
      
      return { ...link, url: cleanedUrl };
    });
  }, [profileData.links]);

  // Preload avatar image for faster rendering
  useEffect(() => {
    if (profileData?.avatar && profileData.avatar.trim() && 
        !profileData.avatar.startsWith('data:') &&
        (profileData.avatar.startsWith('http://') || profileData.avatar.startsWith('https://'))) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = profileData.avatar;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      return () => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      };
    }
  }, [profileData?.avatar]);

  // Auto-show floating text on initial load, then hide after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFloatingText(true);
    }, 1000);
    
    const hideTimer = setTimeout(() => {
      setShowFloatingText(false);
    }, 6000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Calculate active sections count
  const activeSectionsCount = useMemo(() => {
    let count = 0;
    if ((profileData.showLinks !== false) && profileData.links?.length > 0) count++;
    if ((profileData.showVideos === true) && profileData.videos?.length > 0) count++;
    if ((profileData.showStore === true) && profileData.storeItems?.length > 0) count++;
    return count;
  }, [profileData.showLinks, profileData.links, profileData.showVideos, profileData.videos, profileData.showStore, profileData.storeItems]);

  // Only show tab navigation if more than one section is active
  const showTabNavigation = activeSectionsCount > 1;

  // Set initial active tab based on available content
  useEffect(() => {
    // If only one section is active, auto-select it
    if (activeSectionsCount === 1) {
      if ((profileData.showLinks !== false) && profileData.links?.length > 0) {
        setActiveTab('links');
      } else if ((profileData.showVideos === true) && profileData.videos?.length > 0) {
        setActiveTab('videos');
      } else if ((profileData.showStore === true) && profileData.storeItems?.length > 0) {
        setActiveTab('store');
      }
    } else if (activeSectionsCount > 1) {
      // If multiple sections, default to links if available
      if ((profileData.showLinks !== false) && profileData.links?.length > 0) {
        setActiveTab('links');
      } else if ((profileData.showVideos === true) && profileData.videos?.length > 0) {
        setActiveTab('videos');
      } else if ((profileData.showStore === true) && profileData.storeItems?.length > 0) {
        setActiveTab('store');
      }
    }
  }, [activeSectionsCount, profileData.showLinks, profileData.links, profileData.showVideos, profileData.videos, profileData.showStore, profileData.storeItems]);
  
  // Early return if profileData is not loaded yet
  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }
  
  const t = themes[profileData.theme] || themes.midnight;
  
  // Extract background color from theme and apply to html/body
  useEffect(() => {
    // Extract hex color from Tailwind class (e.g., "bg-[#0a0a0a]" -> "#0a0a0a")
    const bgClass = t.bg;
    let bgColor = '#000000'; // Default fallback
    
    // Match hex colors in brackets: bg-[#hex] or bg-[#hexhex] (3 or 6 digits)
    const hexMatch = bgClass.match(/bg-\[#([0-9a-fA-F]{3,6})\]/i);
    if (hexMatch) {
      const hex = hexMatch[1];
      // Handle 3-digit hex (expand to 6)
      bgColor = hex.length === 3 
        ? `#${hex.split('').map(c => c + c).join('')}`
        : `#${hex}`;
    } else if (bgClass.includes('bg-black')) {
      bgColor = '#000000';
    } else if (bgClass.includes('bg-white')) {
      bgColor = '#ffffff';
    } else {
      // Handle Tailwind color classes (e.g., bg-pink-50, bg-gray-100)
      // Map common Tailwind colors to hex values
      const tailwindColorMap = {
        'pink-50': '#fdf2f8',
        'pink-100': '#fce7f3',
        'gray-50': '#f9fafb',
        'gray-100': '#f3f4f6',
        'gray-900': '#111827',
        'gray-950': '#030712',
      };
      
      // Try to find a matching Tailwind color
      const matchedColor = Object.keys(tailwindColorMap).find(color => 
        bgClass.includes(color)
      );
      
      if (matchedColor) {
        bgColor = tailwindColorMap[matchedColor];
      } else {
        // Fallback based on theme type
        bgColor = t.type === 'dark' ? '#000000' : '#ffffff';
      }
    }
    
    // Apply to html and body
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
    
    // Cleanup: reset on unmount
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, [t.bg, t.type]);
  
  // Helper function to determine proper text color based on background
  const getTextColorForCard = (cardBg, themeType) => {
    // If cardBg is white/light and theme is dark (like brutalist), use black text
    if (cardBg && (cardBg.includes('bg-white') || cardBg.includes('bg-gray-50')) && themeType === 'dark') {
      return 'text-black';
    }
    // If cardBg is dark, use white text
    if (cardBg && (cardBg.includes('bg-[#') || cardBg.includes('bg-black') || cardBg.includes('bg-gray-9'))) {
      // Check if it's a dark background
      const isDarkCard = cardBg.includes('bg-[#0') || cardBg.includes('bg-[#1') || 
                        cardBg.includes('bg-[#2') || cardBg.includes('bg-black');
      if (isDarkCard) {
        return 'text-white';
      }
    }
    // Default: use theme text color
    return t.text;
  };
  
  // Helper function to determine text color for main content (not in cards)
  const getMainTextColor = () => {
    // For dark themes with white card backgrounds (like brutalist), check if main bg is also problematic
    // Brutalist has black bg and white text, which is fine for main content
    // But if we're on a light background with white text, that's a problem
    if (t.type === 'light' && t.text.includes('text-white')) {
      return 'text-gray-900'; // Light theme should never have white text
    }
    if (t.type === 'dark' && t.text.includes('text-white')) {
      return 'text-white'; // Dark theme with white text is fine
    }
    return t.text;
  };
  
  // Helper function to determine text color for showcase items
  const getShowcaseTextColor = (themeType, cardBg) => {
    // For dark themes with light cards, use black text
    if (cardBg && cardBg.includes('bg-white') && themeType === 'dark') {
      return { title: 'text-black', subtitle: 'text-gray-700' };
    }
    // For light themes with dark cards, use white text
    if (cardBg && (cardBg.includes('bg-[#1a1a1a]') || cardBg.includes('bg-black'))) {
      return { title: 'text-white', subtitle: 'text-gray-300' };
    }
    // Default based on theme type
    if (themeType === 'dark') {
      return { title: 'text-white', subtitle: 'text-gray-300' };
    }
    return { title: 'text-black', subtitle: 'text-gray-600' };
  };
  
  // Profile URL for sharing and QR code
  const profileUrl = profileData.username 
    ? `${window.location.origin}/${profileData.username}`
    : window.location.href;
  
  // Update meta tags for social media previews
  useEffect(() => {
    if (profileData) {
      // CRITICAL: Clear any existing meta tags first to prevent stale data
      // This ensures we don't show cached "Sarah Jenkins" or other stale content
      const clearMetaTags = () => {
        const metaTagsToClear = [
          'og:title', 'og:description', 'og:image', 'og:image:secure_url', 
          'og:image:width', 'og:image:height', 'og:image:type',
          'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image',
          'description'
        ];
        metaTagsToClear.forEach(prop => {
          const meta = document.querySelector(`meta[property="${prop}"]`) || 
                       document.querySelector(`meta[name="${prop}"]`);
          if (meta) {
            meta.remove();
          }
        });
      };
      
      // Clear old meta tags first
      clearMetaTags();
      
      // Helper function to update or create meta tags
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                   document.querySelector(`meta[name="${property}"]`);
        
        if (!meta) {
          meta = document.createElement('meta');
          if (property.startsWith('og:') || property.startsWith('article:')) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      // CRITICAL: Only update meta tags if we have valid profile data with a name
      // This prevents showing "Profile" or default content
      if (!profileData.name || !profileData.name.trim()) {
        return; // Don't update meta tags if no valid name
      }
      
      // Title format: {Name}: {Bio/Tagline}: PerAI.me (max 60 chars for SEO)
      // SEO best practice: Keep titles 50-60 characters to avoid truncation in search results
      const name = profileData.name.trim();
      let title = '';
      
      if (profileData.tagline && profileData.tagline.trim()) {
        // Use tagline if available (usually shorter and more descriptive)
        const tagline = profileData.tagline.trim();
        const remainingChars = 60 - name.length - 12; // Reserve space for ": " + ": PerAI.me"
        if (tagline.length <= remainingChars) {
          title = `${name}: ${tagline}: PerAI.me`;
        } else {
          title = `${name}: ${tagline.substring(0, remainingChars - 3)}...: PerAI.me`;
        }
      } else if (profileData.bio && profileData.bio.trim()) {
        // Fallback to bio, but truncate to fit in 60 chars
        const remainingChars = 60 - name.length - 12; // Reserve space for ": " + ": PerAI.me"
        const bioText = profileData.bio.trim();
        if (bioText.length <= remainingChars) {
          title = `${name}: ${bioText}: PerAI.me`;
        } else {
          title = `${name}: ${bioText.substring(0, remainingChars - 3)}...: PerAI.me`;
        }
      } else {
        title = `${name}: PerAI.me`;
      }
      
      // Update document title
      document.title = title;
      
      // Description format: {Tagline} - Chat with {Name}'s AI on PerAI
      let description = '';
      if (profileData.tagline && profileData.tagline.trim()) {
        description = `${profileData.tagline} - Chat with ${name}'s AI on PerAI`;
      } else if (profileData.bio && profileData.bio.trim()) {
        const bioText = profileData.bio.length > 120 
          ? profileData.bio.substring(0, 120) + '...' 
          : profileData.bio;
        description = `${bioText} - Chat with ${name}'s AI on PerAI`;
      } else {
        description = `Chat with ${name}'s AI on PerAI`;
      }
      
      // CRITICAL: Don't use default image - only use if valid avatar exists
      // This prevents showing default image before actual profile loads
      const image = (profileData.avatar && 
                    profileData.avatar.trim() && 
                    !profileData.avatar.startsWith('data:') &&
                    (profileData.avatar.startsWith('http://') || profileData.avatar.startsWith('https://')))
                    ? profileData.avatar.trim()
                    : null;
      
      // Open Graph tags
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      // Only include image tags if we have a valid avatar
      if (image) {
        updateMetaTag('og:image', image);
        updateMetaTag('og:image:secure_url', image);
        updateMetaTag('og:image:width', '1200');
        updateMetaTag('og:image:height', '630');
        updateMetaTag('og:image:type', 'image/jpeg');
      }
      updateMetaTag('og:url', profileUrl);
      updateMetaTag('og:type', 'profile');
      updateMetaTag('og:site_name', 'PerAI.me');
      
      // Twitter Card tags
      updateMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
      updateMetaTag('twitter:title', title);
      updateMetaTag('twitter:description', description);
      if (image) {
        updateMetaTag('twitter:image', image);
      }
      
      // Standard meta tags
      updateMetaTag('description', description);
      
      // Canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', profileUrl);
      
      // Person Schema (JSON-LD) - Remove existing if present
      const existingPersonSchema = document.querySelector('script[type="application/ld+json"][data-person-schema]');
      if (existingPersonSchema) {
        existingPersonSchema.remove();
      }
      
      // Create Person schema
      const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": profileData.name,
        "description": description,
        "url": profileUrl
      };
      
      // Only include image if we have a valid avatar
      if (image) {
        personSchema.image = image;
      }
      
      // Add jobTitle if tagline exists
      if (profileData.tagline) {
        personSchema.jobTitle = profileData.tagline;
      }
      
      // Add sameAs (social links) if available
      if (cleanedLinks && Array.isArray(cleanedLinks) && cleanedLinks.length > 0) {
        personSchema.sameAs = cleanedLinks
          .filter(link => link.url && link.url.trim() !== '')
          .map(link => link.url);
      }
      
      // Add Person schema script
      const personSchemaScript = document.createElement('script');
      personSchemaScript.type = 'application/ld+json';
      personSchemaScript.setAttribute('data-person-schema', 'true');
      personSchemaScript.textContent = JSON.stringify(personSchema);
      document.head.appendChild(personSchemaScript);
    }
    
    // Cleanup: Reset to default when component unmounts
    return () => {
      if (!onBack) { // Only reset if not in preview mode (onBack means preview mode)
        document.title = 'PerAI.me | The Bio That Speaks';
        
        // Clean up canonical link
        const canonicalToRemove = document.querySelector('link[rel="canonical"]');
        if (canonicalToRemove) {
          canonicalToRemove.remove();
        }
        
        // Clean up person schema
        const personSchemaToRemove = document.querySelector('script[type="application/ld+json"][data-person-schema]');
        if (personSchemaToRemove) {
          personSchemaToRemove.remove();
        }
      }
    };
  }, [profileData, onBack]);

  const handleSpotlightClick = () => {
    if (profileData.spotlight?.url) window.open(profileData.spotlight.url, '_blank');
  };

  const handleBookingClick = () => {
    if (profileData.bookingUrl) window.open(profileData.bookingUrl, '_blank');
  };

  // Get subscriber count
  useEffect(() => {
    if (profileOwnerId && profileData && profileData.enableSubscriptions === true) {
      const getCount = async () => {
        try {
          const count = await getSubscriberCount(profileOwnerId);
          setSubscriberCount(count);
        } catch (error) {
          console.error('Error getting subscriber count:', error);
        }
      };
      getCount();
    } else {
      setSubscriberCount(0);
    }
  }, [profileOwnerId, profileData?.enableSubscriptions]);

  // Handle email subscription
  const handleEmailSubscribe = async (e) => {
    e.preventDefault();
    
    if (!profileOwnerId) return;
    
    const email = subscribeEmail.trim().toLowerCase();
    if (!email) {
      setSubscribeError('Please enter an email address');
      return;
    }
    
    setIsSubscribing(true);
    setSubscribeError('');
    setSubscribeSuccess(false);
    
    try {
      const result = await subscribeWithEmail(profileOwnerId, email);
      
      if (result.alreadySubscribed) {
        setSubscribeError('This email is already subscribed');
      } else {
        setSubscribeSuccess(true);
        setSubscribeEmail('');
        setSubscriberCount(prev => prev + 1);
        // Hide form after 3 seconds
        setTimeout(() => {
          setShowSubscribeForm(false);
          setSubscribeSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      setSubscribeError(error.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleShare = async (platform) => {
    const shareText = `Check out ${profileData.name}'s AI-powered profile on PerAI.me!`;
    const encodedUrl = encodeURIComponent(profileUrl);
    const encodedText = encodeURIComponent(shareText);

    const shareUrls = {
      copy: async () => {
        try {
          await navigator.clipboard.writeText(profileUrl);
          alert('Link copied to clipboard!');
        } catch (err) {
          console.error('Failed to copy:', err);
          alert('Failed to copy link.');
        }
      },
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      email: `mailto:?subject=${encodeURIComponent(`Check out ${profileData.name}'s profile`)}&body=${encodedText}%20${encodedUrl}`,
      more: () => navigator.share ? navigator.share({ title: shareText, url: profileUrl }) : alert('Web Share API not supported in this browser.')
    };

    if (platform === 'copy') {
      await shareUrls.copy();
    } else if (platform === 'more') {
      await shareUrls.more();
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className={`relative font-sans transition-colors duration-700 ${t.bg} flex flex-col items-center overflow-x-hidden w-full min-h-full`}>
      {/* Background Ambience */}
      <div className={`absolute top-0 left-0 w-full h-[50vh] bg-gradient-to-b ${t.accent} opacity-30 blur-[120px]`}></div>
      
      {/* Content Container */}
      <div className="relative z-10 w-full max-w-md px-4 sm:px-6 pt-3 sm:pt-5 pb-0 flex flex-col items-center overflow-x-hidden">
        
        {/* Header Row - Editor button and Share button */}
        <div className="w-full flex items-center justify-between mb-2 sm:mb-3">
          {/* Editor Button - Left */}
          {onBack && (
            <button onClick={onBack} className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest backdrop-blur-md border hover:scale-105 transition-transform flex items-center ${t.type === 'dark' ? 'bg-white/10 text-white border-white/10' : 'bg-white/60 text-black border-white/40'}`}>
              <ArrowLeft size={10} className="sm:w-3 sm:h-3 mr-1 sm:mr-2"/> Editor
            </button>
          )}
          {!onBack && <div />} {/* Spacer when no Editor button */}
          
          {/* Share Button - Right */}
          <button 
            onClick={() => setShowShareModal(true)}
            className="p-2.5 sm:p-3 rounded-full bg-black hover:bg-gray-800 transition-colors shadow-lg"
            title="Share profile"
          >
            <Share2 size={20} className="text-white" />
          </button>
        </div>

        {/* Profile Header (Centered & Glowing) */}
        <div className="flex flex-col items-center mb-2 sm:mb-3">
          <div className="relative mb-2 sm:mb-2.5 group cursor-pointer" onClick={() => setShowChat(true)}>
            <div className={`absolute -inset-1 bg-gradient-to-tr ${t.accent} rounded-full opacity-60 blur-md group-hover:opacity-100 transition duration-500`}></div>
            <img
              src={profileData.avatar || defaultAvatarDataUrl(profileData.name, 256)}
              className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white/10 shadow-2xl"
              alt="Profile"
              width={128}
              height={128}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              onError={(e) => {
                e.target.src = defaultAvatarDataUrl(profileData.name, 256);
              }}
            />
          </div>

          <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-tight text-center mb-0.5 px-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-95' : 'opacity-100'}`}>{profileData.name}</h1>
          <p className={`text-center text-[10px] sm:text-xs font-medium leading-tight mb-1 px-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-85' : 'opacity-75'}`}>{profileData.tagline}</p>
          
          {/* Subscriber Count & Email Subscribe Form */}
          {profileData && profileData.enableSubscriptions === true && (
            <div className="flex flex-col items-center space-y-1.5 mt-1.5 mb-1">
              {/* Only show count if showSubscriberCount is enabled */}
              {profileData.showSubscriberCount !== false && (
                <div className={`text-center ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-90' : 'opacity-80'}`}>
                  <p className={`text-base sm:text-sm font-bold ${getMainTextColor()}`}>{subscriberCount || 0}</p>
                  <p className={`text-xs sm:text-[10px] uppercase tracking-wider ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-80' : 'opacity-70'}`}>Subscribers</p>
                </div>
              )}
              
              {/* Email Subscribe Form */}
              {!showSubscribeForm ? (
                <button
                  onClick={() => setShowSubscribeForm(true)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all hover:opacity-90 ${t.buttonBg}`}
                >
                  Subscribe
                </button>
              ) : (
                <form onSubmit={handleEmailSubscribe} className="flex flex-col items-center space-y-2 w-full max-w-xs">
                  <input
                    type="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={isSubscribing}
                    className={`w-full px-4 py-2 rounded-full text-sm border-2 outline-none transition-all ${
                      subscribeError 
                        ? 'border-red-500 bg-red-50' 
                        : subscribeSuccess
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 bg-white focus:border-indigo-500'
                    } disabled:opacity-50`}
                    required
                  />
                  {subscribeError && (
                    <p className="text-xs text-red-500 text-center">{subscribeError}</p>
                  )}
                  {subscribeSuccess && (
                    <p className="text-xs text-green-600 text-center font-medium">✓ Subscribed successfully!</p>
                  )}
                  <div className="flex items-center space-x-2 w-full">
                    <button
                      type="submit"
                      disabled={isSubscribing}
                      className="flex-1 px-4 py-2 rounded-full text-sm font-bold transition-all bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {isSubscribing ? (
                        <Loader2 size={16} className="animate-spin inline" />
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSubscribeForm(false);
                        setSubscribeEmail('');
                        setSubscribeError('');
                        setSubscribeSuccess(false);
                      }}
                      className="px-4 py-2 rounded-full text-sm font-bold transition-all bg-gray-200 text-gray-700 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* --- STACKED ACTION BUTTONS --- */}
        <div className="w-full space-y-2 mb-3 sm:mb-4">
          
          {/* 1. BOOKING (White Pill) */}
          {profileData.showBooking === true && profileData.bookingUrl && (
            <button 
              onClick={handleBookingClick}
              className="w-full py-2.5 sm:py-3 bg-white text-black rounded-xl sm:rounded-2xl font-bold flex items-center justify-center space-x-2 shadow-lg hover:scale-[1.02] transition-transform animate-in slide-in-from-bottom-2 text-sm sm:text-base"
            >
              <Calendar size={18} className="sm:w-5 sm:h-5 text-black"/>
              <span>Book a Call</span>
            </button>
          )}

          {/* 2. ASK AI (White Card) */}
          <button 
            onClick={() => setShowChat(true)}
            className="w-full bg-white text-black p-3.5 sm:p-4 rounded-xl sm:rounded-[1.5rem] shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-between group"
          >
            <div className="text-left flex-1 min-w-0">
              <span className="block text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-gray-500 mb-0.5">
                {profileData.chatButtonSubtext || "CONNECT INSTANTLY"}
              </span>
              <span className="block text-sm sm:text-base font-bold truncate">
                {profileData.chatButtonText || "Say hello 👋"}
              </span>
            </div>
            <ChevronRight size={20} className="sm:w-6 sm:h-6 text-gray-400 group-hover:text-black transition-colors shrink-0 ml-2" />
          </button>

          {/* Toggle Controls for Links/Videos/Store - Only show if more than one section is active */}
          {showTabNavigation && (
            <div className="w-full mb-3 sm:mb-4 flex justify-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full p-1 border border-white/20">
                {(profileData.showLinks !== false) && profileData.links?.length > 0 && (
                  <button
                    onClick={() => setActiveTab('links')}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                      activeTab === 'links'
                        ? 'bg-black text-white shadow-lg'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Links
                  </button>
                )}
                {(profileData.showVideos === true) && profileData.videos?.length > 0 && (
                  <button
                    onClick={() => setActiveTab('videos')}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                      activeTab === 'videos'
                        ? 'bg-black text-white shadow-lg'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Videos
                  </button>
                )}
                {(profileData.showStore === true) && profileData.storeItems?.length > 0 && (
                  <button
                    onClick={() => setActiveTab('store')}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                      activeTab === 'store'
                        ? 'bg-black text-white shadow-lg'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Store
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 3. FEATURED (Dark Card) */}
          {profileData.showLinks !== false && profileData.showSpotlightAndListings === true && profileData.spotlight?.title && (() => {
            // Only show when Links tab is active (or when there's no tab navigation)
            const shouldShow = showTabNavigation ? activeTab === 'links' : true;
            if (!shouldShow) return null;
            
            return (
              <button 
                onClick={handleSpotlightClick}
                className={`w-full p-3.5 sm:p-4 rounded-xl sm:rounded-[1.5rem] shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-between group border ${t.type === 'dark' ? 'bg-[#1a1a1a] border-white/10' : 'bg-black text-white border-black'}`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {profileData.spotlight?.image && (
                    <img 
                      src={normalizeImageUrl(profileData.spotlight.image)} 
                      alt={profileData.spotlight?.title || 'Spotlight'}
                      className={`w-11 h-11 sm:w-14 sm:h-14 rounded-lg object-cover mr-2.5 sm:mr-3 shrink-0 border ${t.type === 'dark' ? 'border-white/10' : 'border-white/20'}`}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        console.error('Spotlight image failed to load on public profile:', profileData.spotlight?.image);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="text-left flex-1 min-w-0">
                    <span className={`block text-[8px] sm:text-[9px] font-bold uppercase tracking-widest mb-0.5 ${t.type === 'dark' ? 'text-gray-400' : 'text-gray-400'}`}>Featured</span>
                    <span className={`block text-sm sm:text-base font-bold truncate ${t.type === 'dark' ? 'text-white' : 'text-white'}`}>{profileData.spotlight?.title}</span>
                    {profileData.spotlight?.subtitle && (
                      <span className={`block text-xs sm:text-sm mt-1 opacity-80 ${t.type === 'dark' ? 'text-gray-300' : 'text-gray-300'}`}>{profileData.spotlight.subtitle}</span>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} className="sm:w-6 sm:h-6 text-gray-500 group-hover:text-white transition-colors shrink-0 ml-2" />
              </button>
            );
          })()}

          {/* NON-FEATURED SHOWCASES */}
          {profileData.showLinks !== false && profileData.showSpotlightAndListings === true && profileData.showcases && profileData.showcases.length > 0 && (() => {
            // Only show when Links tab is active (or when there's no tab navigation)
            const shouldShow = showTabNavigation ? activeTab === 'links' : true;
            if (!shouldShow) return null;
            
            return (
              <div className="w-full space-y-2.5">
                {profileData.showcases
                  .filter(showcase => showcase.title && showcase.title.trim()) // Only show if has title (URL is optional)
                  .map((showcase, index) => {
                    const hasValidUrl = showcase.url && showcase.url.trim() && !showcase.url.includes('https://...') && !showcase.url.includes('placeholder');
                    const showcaseCardBg = t.type === 'dark' ? 'bg-[#1a1a1a]' : 'bg-white';
                    const showcaseTextColors = getShowcaseTextColor(t.type, showcaseCardBg);
                    const Component = hasValidUrl ? 'button' : 'div';
                    const props = hasValidUrl ? {
                      onClick: () => window.open(showcase.url, '_blank'),
                      className: `w-full p-3.5 sm:p-4 rounded-xl sm:rounded-[1.5rem] shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-between group border ${t.type === 'dark' ? 'bg-[#1a1a1a] border-white/10 hover:bg-[#252525]' : 'bg-white border-black/10 hover:bg-gray-50'} cursor-pointer`
                    } : {
                      className: `w-full p-3.5 sm:p-4 rounded-xl sm:rounded-[1.5rem] shadow-lg flex items-center justify-between group border ${t.type === 'dark' ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-black/10'} opacity-75`
                    };
                    
                    return (
                    <Component
                      key={index}
                      {...props}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        {showcase.image && (
                          <img 
                            src={showcase.image} 
                            alt={showcase.title}
                            className={`w-11 h-11 sm:w-14 sm:h-14 rounded-lg object-cover mr-2.5 sm:mr-3 shrink-0 border ${t.type === 'dark' ? 'border-white/10' : 'border-gray-200'}`}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="text-left flex-1 min-w-0">
                          <span className={`block text-base sm:text-lg font-bold truncate ${showcaseTextColors.title}`}>
                            {showcase.title}
                          </span>
                          {showcase.subtitle && (
                            <span className={`block text-xs sm:text-sm mt-1 ${showcaseTextColors.subtitle}`}>
                              {showcase.subtitle}
                            </span>
                          )}
                        </div>
                      </div>
                      {hasValidUrl && (
                        <ChevronRight size={20} className={`sm:w-6 sm:h-6 shrink-0 ml-2 ${t.type === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-black'} transition-colors`} />
                      )}
                    </Component>
                  );
                  })}
              </div>
            );
          })()}

          {/* Videos Section - Above Bio */}
          {(profileData.showVideos === true) && profileData.videos?.length > 0 && (() => {
            const shouldShow = showTabNavigation ? activeTab === 'videos' : true;
            
            // Early return if we shouldn't show this section
            if (!shouldShow) return null;
            
            return (
              <div className="w-full space-y-3 mb-3 sm:mb-4">
                <h3 className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-2.5 pl-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-70' : 'opacity-60'}`}>Videos</h3>
                {profileData.videos.map((video, index) => {
                  // Extract YouTube video ID from URL
                  const getYouTubeId = (url) => {
                    if (!url) return null;
                    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                    const match = url.match(regExp);
                    return (match && match[2].length === 11) ? match[2] : null;
                  };
                  
                  const videoId = getYouTubeId(video.youtubeUrl);
                  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;
                  
                  return (
                    <div key={video.id || index} className="w-full">
                      {embedUrl ? (
                        <div className="relative w-full aspect-video rounded-xl sm:rounded-2xl overflow-hidden border border-white/10">
                          <iframe
                            src={embedUrl}
                            title={video.title || 'YouTube video'}
                            className="absolute top-0 left-0 w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <div className={`p-4 rounded-xl ${t.type === 'dark' ? 'bg-white/10' : 'bg-gray-100'} ${getMainTextColor()} opacity-70 text-sm`}>
                          Invalid YouTube URL
                        </div>
                      )}
                      {video.title && (
                        <h4 className={`mt-2 text-sm font-bold ${getMainTextColor()}`}>{video.title}</h4>
                      )}
                      {video.description && (
                        <p className={`mt-1 text-xs ${getMainTextColor()} opacity-70`}>{video.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Store Section - Above Bio */}
          {(profileData.showStore === true) && profileData.storeItems?.length > 0 && (() => {
            const shouldShow = showTabNavigation ? activeTab === 'store' : true;
            
            // Early return if we shouldn't show this section
            if (!shouldShow) return null;
            
            return (
              <div className="w-full space-y-3 mb-3 sm:mb-4">
                <h3 className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-2.5 pl-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-70' : 'opacity-60'}`}>Store</h3>
                <div className="grid grid-cols-2 gap-3">
                  {profileData.storeItems.map((item, index) => (
                    <button
                      key={item.id || index}
                      onClick={() => item.url && window.open(item.url, '_blank', 'noopener,noreferrer')}
                      className={`group relative p-3 rounded-xl sm:rounded-2xl border transition-all hover:scale-[1.02] ${
                        t.type === 'dark' 
                          ? 'bg-[#1a1a1a] border-white/10 hover:bg-[#252525]' 
                          : 'bg-white border-black/10 hover:bg-gray-50'
                      }`}
                    >
                      {item.badge && (
                        <div className="absolute top-2 left-2 bg-yellow-400 text-black text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
                          {item.badge}
                        </div>
                      )}
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full aspect-square object-cover rounded-lg mb-2"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <h4 className={`text-sm font-bold text-left ${getMainTextColor()} truncate`}>{item.title}</h4>
                      {item.price && (
                        <p className={`text-xs mt-1 ${getMainTextColor()} opacity-70`}>{item.price}</p>
                      )}
                      {item.url && (
                        <div className="mt-2 w-full bg-[#C1FF00] text-black text-xs font-bold py-1.5 rounded-lg text-center">
                          Buy
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>

        {/* Bio Text — short bios stay centered (taglines), longer bios
            left-align for readability since centered prose is hard to scan. */}
        <p className={`text-xs sm:text-sm font-medium leading-relaxed mb-3 sm:mb-4 px-2 sm:px-4 max-w-md mx-auto ${(profileData.bio || '').trim().length > 90 ? 'text-left' : 'text-center'} ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-90' : 'opacity-80'}`}>
          {profileData.bio}
        </p>

        {/* Tab Content Container - All tabs kept mounted for smooth transitions and iframe preservation */}
        <div className="w-full">
          {/* Links Section - Tabbed View - Only show custom links (not social media) */}
          {(profileData.showLinks !== false) && (() => {
            // Filter out social media links (from PLATFORMS_CONFIG) - they should only show as minimalist icons
            const customLinks = cleanedLinks?.filter(link => {
              const platform = PLATFORMS_CONFIG.find(p => p.id === link.iconId);
              return !platform; // Only show links that are NOT in PLATFORMS_CONFIG
            }) || [];
            
            // Only render if there are custom links to show
            if (customLinks.length === 0) return null;
            
            // If only one section is active, always show it (no tab switching needed)
            // Otherwise, only show when links tab is active
            const shouldShow = showTabNavigation ? activeTab === 'links' : true;
            
            // Early return if we shouldn't show this section
            if (!shouldShow) return null;
            
            return (
              <div className="w-full space-y-2 mb-3 sm:mb-4">
                {customLinks.map((link, i) => (
                  <button
                    key={i}
                    onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                    className={`w-full p-3.5 sm:p-4 rounded-xl sm:rounded-[1.5rem] shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-between group border ${t.type === 'dark' ? 'bg-[#1a1a1a] border-white/10 hover:bg-[#252525]' : 'bg-white border-black/10 hover:bg-gray-50'} cursor-pointer`}
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      {link.iconId && (
                        <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-lg object-cover mr-2.5 sm:mr-3 shrink-0 border flex items-center justify-center ${t.type === 'dark' ? 'border-white/10' : 'border-gray-200'} bg-gray-500`}>
                          <LinkIcon size={20} className="text-white" />
                        </div>
                      )}
                      <div className="text-left flex-1 min-w-0">
                        <span className={`block text-base sm:text-lg font-bold truncate ${t.type === 'dark' ? 'text-white' : 'text-black'}`}>
                          {link.title || 'Link'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={20} className={`sm:w-6 sm:h-6 shrink-0 ml-2 ${t.type === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-black'} transition-colors`} />
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        {/* --- SOCIAL ROW (Minimalist Icons) - Always Show --- */}
        {cleanedLinks && cleanedLinks.length > 0 && (
          <div className="flex items-center justify-center space-x-2.5 sm:space-x-3 flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {cleanedLinks.map((link, i) => {
            const p = PLATFORMS_CONFIG.find(pc => pc.id === link.iconId) || PLATFORMS_CONFIG[0];
            // Brand-color social icons (Instagram orange-pink gradient,
            // YouTube red, etc.) clash hard with dark themes — four
            // distinct candy colors against #0a0a0a reads like a child's
            // app. Force monochrome on dark themes regardless of the
            // user's saved socialIconStyle. On light themes, respect the
            // brand-vs-mono preference. See design audit 2026-04-25 D4.
            const useBrandColors = profileData.socialIconStyle === 'brand' && t.type !== 'dark';
            // Check if cardBg is white and type is dark (brutalist theme) - use black text
            const isWhiteCardOnDark = !useBrandColors && t.cardBg.includes('bg-white') && t.type === 'dark';
            // Determine icon color based on background
            const iconColor = useBrandColors 
              ? 'text-white' 
              : isWhiteCardOnDark
                ? 'text-black'
                : t.type === 'dark' 
                  ? 'text-white' 
                  : 'text-black';
            
            return (
              <a 
                key={i} 
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border backdrop-blur-md transition-all hover:-translate-y-1 hover:shadow-lg ${
                  useBrandColors 
                    ? `${p.color} text-white border-transparent` 
                    : isWhiteCardOnDark
                      ? `${t.cardBg} border-white/10 hover:bg-white/10 ${iconColor}`
                      : `${t.cardBg} ${t.type === 'dark' ? 'border-white/10 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'} ${iconColor}`
                }`}
              >
                <div className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`}>{p.icon}</div>
              </a>
            )
          })}
        </div>
        )}

        {/* Contact Information Section */}
        {profileData.showContactInfo === true && 
         ((profileData.contactInfo?.phones && profileData.contactInfo.phones.length > 0) || 
          (profileData.contactInfo?.emails && profileData.contactInfo.emails.length > 0)) && (
          <div className="w-full mt-4 sm:mt-5">
            <h3 className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-2.5 pl-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-70' : 'opacity-60'}`}>Contact</h3>
            {/* Contact card is always white to match other buttons (Book a Call, Chat, etc.) */}
            {(() => {
              // Always use white background for contact card to match other action buttons
              const cardTextColor = 'text-black'; // Black text on white card for best contrast
              const iconBg = 'bg-black/10'; // Subtle black background for icons
              const borderColor = 'border-black/10'; // Subtle black border
              const iconColor = 'text-black'; // Black icons to match text
              
              return (
                <div className={`space-y-2.5 bg-white ${borderColor} border rounded-xl sm:rounded-2xl p-3.5 sm:p-4`}>
                  
                  {/* Phone Numbers */}
                  {profileData.contactInfo?.phones && profileData.contactInfo.phones.length > 0 && (
                    <div className="space-y-2">
                      {profileData.contactInfo.phones.map((phone, index) => (
                        phone.number && (
                          <a
                            key={index}
                            href={`tel:${phone.number.replace(/\s/g, '')}`}
                            className={`flex items-center space-x-3 group ${cardTextColor} hover:opacity-80 transition-opacity`}
                          >
                            <div className={`p-2 rounded-lg ${iconBg} ${iconColor}`}>
                              <Phone size={16} className="sm:w-4 sm:h-4" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm sm:text-base font-medium truncate ${cardTextColor}`}>{phone.number}</p>
                              {phone.type && (
                                <p className={`text-[10px] sm:text-xs opacity-60 capitalize ${cardTextColor}`}>
                                  {phone.type}
                                </p>
                              )}
                            </div>
                          </a>
                        )
                      ))}
                    </div>
                  )}

                  {/* Email Addresses */}
                  {profileData.contactInfo?.emails && profileData.contactInfo.emails.length > 0 && (
                    <div className={`space-y-2 ${profileData.contactInfo?.phones && profileData.contactInfo.phones.length > 0 ? 'pt-3 border-t ' + borderColor : ''}`}>
                      {profileData.contactInfo.emails.map((email, index) => (
                        email.email && (
                          <a
                            key={index}
                            href={`mailto:${email.email}`}
                            className={`flex items-center space-x-3 group ${cardTextColor} hover:opacity-80 transition-opacity`}
                          >
                            <div className={`p-2 rounded-lg ${iconBg} ${iconColor}`}>
                              <Mail size={16} className="sm:w-4 sm:h-4" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm sm:text-base font-medium truncate ${cardTextColor}`}>{email.email}</p>
                              {email.type && (
                                <p className={`text-[10px] sm:text-xs opacity-60 capitalize ${cardTextColor}`}>
                                  {email.type}
                                </p>
                              )}
                            </div>
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Media Feed (Conditionally Rendered) */}
        {profileData.showMemories && profileData.media.length > 0 && (
          <div className="w-full mt-4 sm:mt-5">
            <h3 className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-2.5 pl-2 ${getMainTextColor()} ${t.type === 'dark' ? 'opacity-70' : 'opacity-60'}`}>Recent Memories</h3>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {(profileData.media || []).map((m, i) => (
                <div key={i} className={`group relative aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden border ${t.type === 'dark' ? 'border-white/10' : 'border-black/5'}`}>
                  {m.type === 'video' ? (
                    <video 
                      src={m.url} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      controls
                      muted
                      playsInline
                    />
                  ) : (
                    <img 
                      src={m.url} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      alt={m.caption || 'Memory'}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=500';
                      }}
                    />
                  )}
                  {/* Caption is now always visible when present (mobile
                      doesn't have hover; the previous opacity-0
                      hover-only treatment hid captions entirely on
                      touch devices). Desktop hover still brightens. */}
                  {m.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent flex items-end p-2 sm:p-3 transition-all opacity-90 group-hover:opacity-100">
                      <p className="text-white text-[10px] sm:text-xs font-medium line-clamp-2 leading-snug drop-shadow">{m.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Promotional Section - Inside Content */}
        <div className="w-full mt-4 sm:mt-5 pb-5 sm:pb-7">
          <div className="text-center">
            {/* Main text - all on one line - Clickable */}
            <a 
              href="https://perai.me" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`text-base sm:text-lg font-medium mb-4 sm:mb-5 ${getMainTextColor()} block hover:opacity-80 transition-opacity cursor-pointer`}
            >
              Join{' '}
              <span className="font-bold text-lg sm:text-xl">
                {profileData.username 
                  ? profileData.username.charAt(0).toUpperCase() + profileData.username.slice(1)
                  : 'us'
                }
              </span>{' '}
              on{' '}
              <span className="ai-shimmer text-xl sm:text-2xl font-bold relative inline-block">
                <span className="ai-shimmer-particles">
                  {/* Sparkling particles */}
                  {[...Array(12)].map((_, i) => {
                    const angle = (i * 30) * (Math.PI / 180);
                    const radius = 60 + (i % 3) * 15;
                    const tx = Math.cos(angle) * radius;
                    const ty = Math.sin(angle) * radius;
                    return (
                      <span
                        key={i}
                        className="ai-shimmer-particle"
                        style={{
                          '--tx': `${tx}px`,
                          '--ty': `${ty}px`,
                          left: '50%',
                          top: '50%',
                          animationDelay: `${i * 0.2}s`,
                          animationDuration: `${2 + (i % 3) * 0.5}s`
                        }}
                      />
                    );
                  })}
                </span>
                {/* Swirling trails */}
                {[...Array(3)].map((_, i) => (
                  <span
                    key={i}
                    className="ai-shimmer-trail"
                    style={{
                      animationDelay: `${i * 1.3}s`,
                      animationDuration: `${4 + i * 0.5}s`
                    }}
                  />
                ))}
                PerAI.me
              </span>
            </a>
            
            {/* Footer Links */}
            <div className={`flex items-center justify-center gap-2 text-xs ${getMainTextColor()} opacity-70`}>
              <a href="/comparison" className="hover:opacity-100 underline">vs Linktree</a>
              <span className="opacity-40">•</span>
              <a href="/privacy" className="hover:opacity-100 underline">Privacy Policy</a>
              <span className="opacity-40">•</span>
              <a href="/terms" className="hover:opacity-100 underline">Terms</a>
              <span className="opacity-40">•</span>
              <a href="https://www.instagram.com/perai.me/" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 underline">Instagram</a>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-md shadow-2xl animate-in zoom-in-95 relative overflow-hidden">
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-100">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {showQRCode ? 'QR Code' : 'Share Profile'}
              </h3>
              <button onClick={() => {
                setShowShareModal(false);
                setShowQRCode(false);
              }} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                <X size={18} />
              </button>
            </div>
            
            <div className="bg-gray-800 p-6 sm:p-8 rounded-b-2xl">
              {showQRCode ? (
                // QR Code View
                <div className="flex flex-col items-center">
                  <div className="bg-white p-6 rounded-2xl mb-4">
                    <Suspense fallback={<div className="w-64 h-64 flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" size={32} /></div>}>
                      <QRCodeSVG
                        value={profileUrl}
                        size={256}
                        level="H"
                        includeMargin={true}
                      />
                    </Suspense>
                  </div>
                  <p className="text-white text-sm mb-2 font-medium">Scan to visit</p>
                  <p className="text-gray-400 text-xs mb-4 text-center break-all px-4">
                    {profileUrl}
                  </p>
                  <button
                    onClick={() => setShowQRCode(false)}
                    className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Back to Share Options
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex flex-col items-center mb-6">
                    <img
                      src={profileData.avatar || defaultAvatarDataUrl(profileData.name, 160)}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/20 shadow-lg mb-3"
                      alt="Profile Avatar"
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = defaultAvatarDataUrl(profileData.name, 160);
                      }}
                    />
                    <p className="text-white text-lg sm:text-xl font-bold">{profileData.name}</p>
                    <p className="text-gray-400 text-sm mt-1">perai.me/{profileData.username}</p>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3 sm:gap-4 mb-6">
                    {/* Copy Link */}
                    <button onClick={() => handleShare('copy')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                        <LinkIcon size={24} />
                      </div>
                      <span className="text-xs font-medium">Copy</span>
                    </button>
                    
                    {/* QR Code */}
                    <button 
                      onClick={() => setShowQRCode(true)} 
                      className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors"
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                        <QrCode size={24} />
                      </div>
                      <span className="text-xs font-medium">QR Code</span>
                    </button>
                    
                    {/* Twitter */}
                <button onClick={() => handleShare('twitter')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                    <Twitter size={24} />
                  </div>
                  <span className="text-xs font-medium">X</span>
                </button>
                {/* Facebook */}
                <button onClick={() => handleShare('facebook')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                    <Facebook size={24} />
                  </div>
                  <span className="text-xs font-medium">Facebook</span>
                </button>
                {/* WhatsApp */}
                <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                    <MessageCircle size={24} />
                  </div>
                  <span className="text-xs font-medium">WhatsApp</span>
                </button>
                {/* LinkedIn */}
                <button onClick={() => handleShare('linkedin')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                    <Linkedin size={24} />
                  </div>
                  <span className="text-xs font-medium">LinkedIn</span>
                </button>
                {/* Email */}
                <button onClick={() => handleShare('email')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                    <Mail size={24} />
                  </div>
                  <span className="text-xs font-medium">Email</span>
                </button>
                    {/* More */}
                    <button onClick={() => handleShare('more')} className="flex flex-col items-center space-y-1 text-white hover:text-gray-300 transition-colors">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                        <MoreHorizontal size={24} />
                      </div>
                      <span className="text-xs font-medium">More</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat Overlay */}
      {showChat && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-md animate-in fade-in"
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
          onClick={(e) => {
            // Close on backdrop click (mobile only)
            if (window.innerWidth < 640 && e.target === e.currentTarget) {
              setShowChat(false);
            }
          }}
        >
          <div className="w-full h-[100dvh] sm:h-[75vh] sm:w-full sm:max-w-2xl flex flex-col overflow-hidden relative">
            <ChatInterface profileData={profileData} onClose={() => setShowChat(false)} onBack={onBack} profileOwnerId={profileOwnerId} user={user} />
          </div>
          
          {/* Floating Go Back Button for Mobile - Outside container */}
          {onBack && (
            <button 
              onClick={onBack} 
              className={`sm:hidden fixed top-3 left-3 z-[70] p-3.5 rounded-full transition-all bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 shadow-2xl backdrop-blur-md`}
              aria-label="Go back"
              title="Go back"
            >
              <ArrowLeft size={26} strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      {/* Floating Chatbot Button - Only show if enabled */}
      {!showChat && profileData.showChatbotOnProfile === true && (
        <div 
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex flex-col items-end gap-3"
          onMouseEnter={() => setShowFloatingText(true)}
          onMouseLeave={() => setShowFloatingText(false)}
        >
          {/* Animated Text Banner */}
          {showFloatingText && (
            <div className="mb-2 animate-in slide-in-from-right-2 fade-in duration-300">
              <div className="relative">
                {/* Speech bubble tail */}
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white/95 transform rotate-45 border-r-2 border-b-2 border-indigo-200"></div>
                {/* Text bubble */}
                <div className="relative px-4 py-2.5 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border-2 border-indigo-200">
                  <p 
                    className="text-xs sm:text-sm font-bold text-indigo-600 whitespace-nowrap"
                    style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      letterSpacing: '0.3px'
                    }}
                  >
                    {profileData.chatButtonText || "Say hello 👋"}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Floating Chat Button */}
          <button
            onClick={() => setShowChat(true)}
            className="relative group focus:outline-none focus:ring-4 focus:ring-indigo-300/50 rounded-full transition-all duration-300 hover:scale-110 active:scale-95"
            aria-label={`Open chat - ${profileData.chatButtonText || "Say hello"}`}
            title={profileData.chatButtonText || "Say hello 👋"}
          >
            {/* Pulse animation rings - only show when not hovering */}
            {!showFloatingText && (
              <>
                <div className="absolute inset-0 rounded-full bg-indigo-400/60 opacity-75 animate-ping" style={{ animationDuration: '2s' }}></div>
                <div className="absolute inset-0 rounded-full bg-indigo-300/40 opacity-50 animate-pulse" style={{ animationDuration: '2s' }}></div>
              </>
            )}
            
            {/* Subtle breathing animation */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-400/20 to-purple-500/20 animate-pulse" style={{ animationDuration: '3s' }}></div>
            
            {/* Main button with profile picture */}
            <div className="relative w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 rounded-full border-4 border-white shadow-2xl overflow-visible bg-gradient-to-br from-indigo-400 to-purple-500 transition-transform duration-300 group-hover:shadow-indigo-500/50">
              {/* Profile picture container with overflow hidden */}
              <div className="w-full h-full rounded-full overflow-hidden">
                {profileData.avatar ? (
                  <>
                    <img
                      src={profileData.avatar}
                      alt="Chat"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                    <div 
                      className="w-full h-full flex items-center justify-center hidden"
                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                    >
                      <MessageCircle size={24} className="sm:w-7 sm:h-7 text-white" />
                    </div>
                  </>
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                  >
                    <MessageCircle size={24} className="sm:w-7 sm:h-7 text-white" />
                  </div>
                )}
              </div>
              
              {/* Active indicator dot - positioned outside the image container */}
              <div className="absolute top-0 right-0 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow-lg transform translate-x-1/4 -translate-y-1/4">
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

